import React, { useEffect, useState, useCallback } from 'react';
import { 
  getAllUsers,
  getAuditLogs,
  getAuditEventTypes,
  AuditLogFilters,
  AuditLog
} from '../services/api';
import { FiClock, FiUser, FiUserCheck, FiFileText, FiMessageSquare, FiCalendar, FiMapPin, FiCheckCircle, FiX, FiChevronDown, FiChevronUp, FiFilter, FiSearch } from 'react-icons/fi';
import './AuditLogs.css';

// Type-safe icon components
const IconClock = FiClock as any;
const IconUser = FiUser as any;
const IconUserCheck = FiUserCheck as any;
const IconFileText = FiFileText as any;
const IconMessageSquare = FiMessageSquare as any;
const IconCalendar = FiCalendar as any;
const IconMapPin = FiMapPin as any;
const IconCheckCircle = FiCheckCircle as any;
const IconX = FiX as any;
const IconChevronDown = FiChevronDown as any;
const IconChevronUp = FiChevronUp as any;

interface TimelineEvent {
  type: string;
  title: string;
  description: string;
  timestamp: string;
  metadata: Record<string, any>;
}

interface UserOption {
  id: number;
  name: string;
  phoneNumber: string;
  role: string;
}

const AuditLogs: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string>('');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [technicians, setTechnicians] = useState<UserOption[]>([]);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  
  // All logs view state
  const [allLogs, setAllLogs] = useState<AuditLog[]>([]);
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 0,
    size: 50
  });
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
  const [entityIdFilter, setEntityIdFilter] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string>('');

  const loadUsersAndTechnicians = async () => {
    setLoadingUsers(true);
    try {
      const allUsers = await getAllUsers();
      const usersList = allUsers.filter((u: UserOption) => u.role === 'USER');
      const techniciansList = allUsers.filter((u: UserOption) => u.role === 'TECHNICIAN');
      setUsers(usersList);
      setTechnicians(techniciansList);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadEventTypes = async () => {
    try {
      const types = await getAuditEventTypes();
      setEventTypes(types);
    } catch (error) {
      console.error('Failed to load event types:', error);
    }
  };

  const loadAllLogs = useCallback(async () => {
    try {
      setLoading(true);
      const updatedFilters: AuditLogFilters = {
        ...filters,
        eventType: selectedEventType || undefined,
        entityType: entityTypeFilter || undefined,
        entityId: entityIdFilter ? parseInt(entityIdFilter) : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        userId: selectedUserId ? parseInt(selectedUserId) : undefined,
        technicianId: selectedTechnicianId ? parseInt(selectedTechnicianId) : undefined,
      };
      
      const result = await getAuditLogs(updatedFilters);
      setAllLogs(result.content);
      setTotalLogs(result.totalElements);
      setCurrentPage(result.currentPage);
    } catch (error: any) {
      console.error('Failed to load audit logs:', error);
      setError(error.response?.data?.error || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, selectedEventType, entityTypeFilter, entityIdFilter, startDate, endDate, selectedUserId, selectedTechnicianId]);

  useEffect(() => {
    loadUsersAndTechnicians();
    loadEventTypes();
  }, []);

  useEffect(() => {
    loadAllLogs();
  }, [filters, loadAllLogs]);

  const handleApplyFilters = () => {
    setFilters({
      ...filters,
      page: 0
    });
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setSelectedEventType('');
    setEntityTypeFilter('');
    setEntityIdFilter('');
    setStartDate('');
    setEndDate('');
    setSelectedUserId('');
    setSelectedTechnicianId('');
    setFilters({ page: 0, size: 50 });
    setCurrentPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
    setCurrentPage(newPage);
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'PROJECT_CREATED':
      case 'PROJECT_UPDATED':
      case 'PROJECT_DELETED':
      case 'PROJECT_STATUS_CHANGED':
      case 'PROJECT_COMPLETED':
        return React.createElement(IconFileText, { size: 20 });
      case 'BID_CREATED':
      case 'BID_ACCEPTED':
      case 'BID_REJECTED':
      case 'BID_WITHDRAWN':
        return React.createElement(IconCheckCircle, { size: 20 });
      case 'CONTRACT_CREATED':
      case 'CONTRACT_SIGNED':
        return React.createElement(IconFileText, { size: 20 });
      case 'VISIT_REQUEST_CREATED':
      case 'VISIT_REQUEST_ACCEPTED':
      case 'VISIT_REQUEST_REJECTED':
      case 'VISIT_REQUEST_DELETED':
        return React.createElement(IconMapPin, { size: 20 });
      case 'APPOINTMENT_CREATED':
      case 'APPOINTMENT_CONFIRMED':
      case 'APPOINTMENT_COMPLETED':
      case 'APPOINTMENT_CANCELLED':
        return React.createElement(IconCalendar, { size: 20 });
      case 'CHAT_MESSAGE_SENT':
      case 'TICKET_MESSAGE_SENT':
        return React.createElement(IconMessageSquare, { size: 20 });
      case 'PHASE_CREATED':
      case 'PHASE_APPROVED':
      case 'PHASE_PAYMENT_REQUESTED':
      case 'PHASE_PAID':
      case 'PHASE_COMPLETED':
      case 'PHASE_DELETED':
        return React.createElement(IconFileText, { size: 20 });
      case 'TICKET_CREATED':
      case 'TICKET_RESOLVED':
      case 'TICKET_CLOSED':
        return React.createElement(IconMessageSquare, { size: 20 });
      case 'FEEDBACK_CREATED':
      case 'FEEDBACK_RESOLVED':
        return React.createElement(IconCheckCircle, { size: 20 });
      default:
        return React.createElement(IconClock, { size: 20 });
    }
  };

  const getEventColor = (type: string) => {
    if (type.includes('ACCEPTED') || type.includes('SIGNED') || type.includes('COMPLETED') || type.includes('RESOLVED') || type === 'CONTRACT_SIGNED') {
      return '#28a745'; // Green for positive actions
    }
    if (type.includes('REJECTED') || type.includes('DELETED') || type.includes('CANCELLED') || type.includes('WITHDRAWN')) {
      return '#dc3545'; // Red for negative actions
    }
    if (type.includes('CREATED') || type.includes('STARTED')) {
      return '#0080E0'; // Blue for creation
    }
    if (type.includes('STATUS') || type.includes('UPDATED')) {
      return '#ffc107'; // Yellow for status changes
    }
    if (type.includes('PAID') || (type.includes('PAYMENT') && !type.includes('REQUESTED'))) {
      return '#17a2b8'; // Cyan for payments
    }
    if (type.includes('PAYMENT_REQUESTED') || type.includes('REQUESTED_PAYMENT')) {
      return '#ff9800'; // Orange for payment requests
    }
    return '#6c757d'; // Gray for others
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleEvent = (index: number) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const getMetadataPreview = (metadata: Record<string, any>): string => {
    const previews: string[] = [];
    
    if (metadata.projectId) previews.push(`Project #${metadata.projectId}`);
    if (metadata.budget || metadata.moneySpent || metadata.proposedBudget) {
      const amount = metadata.budget || metadata.moneySpent || metadata.proposedBudget;
      previews.push(`${Number(amount).toLocaleString()} SAR`);
    }
    if (metadata.status) previews.push(metadata.status);
    if (metadata.fileCount) previews.push(`${metadata.fileCount} file${metadata.fileCount > 1 ? 's' : ''}`);
    if (metadata.attachmentCount) previews.push(`${metadata.attachmentCount} attachment${metadata.attachmentCount > 1 ? 's' : ''}`);
    if (metadata.phaseNumber) previews.push(`Phase ${metadata.phaseNumber}`);
    if (metadata.subject) previews.push(metadata.subject.length > 30 ? metadata.subject.substring(0, 30) + '...' : metadata.subject);
    
    return previews.length > 0 ? previews.join(' • ') : 'Click to view details';
  };

  const convertAuditLogToTimelineEvent = (log: AuditLog, index: number): TimelineEvent => {
    return {
      type: log.eventType,
      title: log.eventType.replace(/_/g, ' '),
      description: log.description,
      timestamp: log.timestamp,
      metadata: {
        ...log.metadata,
        performedById: log.performedById,
        performedByName: log.performedByName,
        userId: log.userId,
        userName: log.userName,
        technicianId: log.technicianId,
        technicianName: log.technicianName,
        entityType: log.entityType,
        entityId: log.entityId,
        projectId: log.projectId,
        oldValue: log.oldValue,
        newValue: log.newValue,
        ipAddress: log.ipAddress,
      }
    };
  };

  return (
    <div className="audit-logs">
      <div className="page-header">
        <h2>{React.createElement(IconClock, { size: 24 })} Audit Logs</h2>
        <p className="subtitle">
          View comprehensive activity logs from the audit_logs table. 
          All system events are tracked including projects, bids, phases, appointments, tickets, and more. 
          Click on any event card to expand and see full details with attachments.
        </p>
      </div>

      <div className="all-logs-filters">
          <div className="filters-header">
            <h3>{React.createElement(FiFilter as any)} Filters</h3>
            <button className="btn-clear-filters" onClick={handleClearFilters}>
              Clear All
            </button>
          </div>
          <div className="filters-grid">
            <div className="form-group">
              <label>Event Type</label>
              <select
                value={selectedEventType}
                onChange={(e) => setSelectedEventType(e.target.value)}
                className="form-select"
              >
                <option value="">All Event Types</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Entity Type</label>
              <input
                type="text"
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                placeholder="e.g., PROJECT, BID, TICKET"
              />
            </div>
            <div className="form-group">
              <label>Entity ID</label>
              <input
                type="number"
                value={entityIdFilter}
                onChange={(e) => setEntityIdFilter(e.target.value)}
                placeholder="Entity ID"
              />
            </div>
            <div className="form-group">
              <label>User</label>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="form-select"
              >
                <option value="">All Users</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id.toString()}>
                    {user.name} (ID: {user.id})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Technician</label>
              <select
                value={selectedTechnicianId}
                onChange={(e) => setSelectedTechnicianId(e.target.value)}
                className="form-select"
              >
                <option value="">All Technicians</option>
                {technicians.map((tech) => (
                  <option key={tech.id} value={tech.id.toString()}>
                    {tech.name} (ID: {tech.id})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Page Size</label>
              <select
                value={filters.size || 50}
                onChange={(e) => setFilters({ ...filters, size: parseInt(e.target.value), page: 0 })}
                className="form-select"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
          </div>
          <button className="btn-apply-filters" onClick={handleApplyFilters} disabled={loading}>
            {React.createElement(FiSearch as any)} Apply Filters
          </button>
        </div>

      {error && (
        <div className="error-message">
          {React.createElement(IconX, { size: 16 })} {error}
        </div>
      )}

      <div className="all-logs-container">
        <div className="logs-header">
            <div className="logs-stats">
              <span>Total Logs: {totalLogs}</span>
              <span>Showing: {allLogs.length}</span>
            </div>
            <div className="pagination-controls">
              <button
                className="btn-page"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 0 || loading}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage + 1} of {Math.ceil(totalLogs / (filters.size || 50))}
              </span>
              <button
                className="btn-page"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalLogs / (filters.size || 50)) - 1 || loading}
              >
                Next
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading audit logs...</p>
            </div>
          ) : allLogs.length === 0 ? (
            <div className="no-interactions">
              <p>No audit logs found with the current filters.</p>
            </div>
          ) : (
            <div className="timeline">
              {allLogs.map((log, index) => {
                const event = convertAuditLogToTimelineEvent(log, index);
                const isExpanded = expandedEvents.has(index);
                const hasMetadata = Object.keys(event.metadata).filter(key => 
                  !['entityId', 'entityType', 'performedById', 'performedByName', 'technicianId', 'technicianName', 'note'].includes(key)
                ).length > 0;
                
                return (
                  <div key={log.id} className="timeline-item">
                    <div className="timeline-marker" style={{ backgroundColor: getEventColor(event.type) }}>
                      {getEventIcon(event.type)}
                    </div>
                    <div className={`timeline-content ${isExpanded ? 'expanded' : ''}`}>
                      <div 
                        className={`timeline-header ${hasMetadata ? 'clickable' : ''}`}
                        onClick={() => hasMetadata && toggleEvent(index)}
                        style={{ cursor: hasMetadata ? 'pointer' : 'default' }}
                      >
                        <div>
                          <h3>{event.title}</h3>
                          {event.metadata.performedByName && (
                            <span className="performed-by">
                              By: {event.metadata.performedByName}
                              {event.metadata.performedById && ` (ID: ${event.metadata.performedById})`}
                            </span>
                          )}
                          {event.metadata.entityType && event.metadata.entityId && (
                            <span className="entity-info">
                              {event.metadata.entityType} #{event.metadata.entityId}
                            </span>
                          )}
                          {!isExpanded && hasMetadata && (
                            <span className="metadata-preview">
                              {getMetadataPreview(event.metadata)}
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span className="timeline-date">{formatDate(event.timestamp)}</span>
                          {hasMetadata && (
                            <span className="expand-icon">
                              {isExpanded ? React.createElement(IconChevronUp, { size: 18 }) : React.createElement(IconChevronDown, { size: 18 })}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="timeline-description">{event.description}</p>
                      {isExpanded && hasMetadata && (
                        <div className="timeline-metadata">
                          {Object.entries(event.metadata).map(([key, value]) => {
                            // Skip some internal fields
                            if (['entityId', 'entityType', 'performedById', 'performedByName', 'technicianId', 'technicianName', 'note'].includes(key)) {
                              return null;
                            }
                            
                            // Format special fields
                            if (key === 'files' && Array.isArray(value)) {
                              return (
                                <div key={key} className="metadata-section">
                                  <strong className="metadata-label">Files ({value.length}):</strong>
                                  <div className="files-list">
                                    {value.map((file: string, idx: number) => (
                                      <a key={idx} href={file} target="_blank" rel="noopener noreferrer" className="file-link">
                                        {file.split('/').pop() || `File ${idx + 1}`}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                            
                            if (key === 'attachments' && Array.isArray(value)) {
                              return (
                                <div key={key} className="metadata-section">
                                  <strong className="metadata-label">Attachments ({value.length}):</strong>
                                  <div className="files-list">
                                    {value.map((file: string, idx: number) => (
                                      <a key={idx} href={file} target="_blank" rel="noopener noreferrer" className="file-link">
                                        {file.split('/').pop() || `Attachment ${idx + 1}`}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                            
                            if (key === 'oldValue' || key === 'newValue') {
                              try {
                                const parsed = typeof value === 'string' ? JSON.parse(value) : value;
                                if (typeof parsed === 'object' && parsed !== null) {
                                  return (
                                    <div key={key} className="metadata-section">
                                      <strong className="metadata-label">{key === 'oldValue' ? 'Previous:' : 'New:'}</strong>
                                      <div className="value-object">
                                        {Object.entries(parsed).map(([k, v]) => (
                                          <span key={k} className="value-item">
                                            <strong>{k}:</strong> {String(v)}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                }
                              } catch (e) {
                                // Not JSON, display as is
                              }
                            }
                            
                            // Format money values
                            if ((key === 'budget' || key === 'moneySpent' || key === 'proposedBudget') && value) {
                              return (
                                <span key={key} className="metadata-tag money-tag">
                                  <strong>{key}:</strong> {Number(value).toLocaleString()} SAR
                                </span>
                              );
                            }
                            
                            // Format dates
                            if (key.includes('Date') || key.includes('At') || key.includes('Time')) {
                              try {
                                const date = new Date(String(value));
                                if (!isNaN(date.getTime())) {
                                  return (
                                    <span key={key} className="metadata-tag date-tag">
                                      <strong>{key}:</strong> {date.toLocaleString()}
                                    </span>
                                  );
                                }
                              } catch (e) {
                                // Not a valid date
                              }
                            }
                            
                            // Regular metadata tag
                            return (
                              <span key={key} className="metadata-tag">
                                <strong>{key}:</strong> {String(value)}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
};

export default AuditLogs;

